import { left, right } from 'fp-ts/lib/Either';
import { Vector } from '../Vector';
import { ZeroArray } from '../util/ZeroArray';
/**
 * a general i-by-j Matrix of numbers
 */
export class Matrix {
    _rows;
    _columns;
    static ERR_CANNOT_CONSTRUCT_INVALID_LENGTH = '[Matrix]: unable to construct Matrix with initial contents of invalid length';
    static ERR_CANNOT_ADD_DIMENSIONS_DIFFER = '[Matrix]: unable to add matrices with differing dimensions';
    static ERR_CANNOT_MULTIPLY_INCOMPATIBLE_DIMENSIONS = '[Matrix]: Unable to multiply matrices with incompatible dimensions';
    static ERR_INDEX_OUT_OF_BOUNDS = '[Matrix]: Cannot retrieve, index provided is out of bounds';
    /**
     *  construct a rows-by-columns sized zero-matrix.
     */
    static zeroMatrix(rows, columns) {
        return new Matrix(rows, columns);
    }
    /**
     * construct a Matrix from an existing array.
     * validates that the array is the correct size, given the number of rows and columns.
     * in the case that the array is the incorrect size, returns an Either.Left,
     * otherwise returns an Either.Right of the constructed Matrix
     */
    static fromArray(rows, columns, array) {
        return Matrix.invalidConstructor(array, rows, columns)
            ? left(Matrix.ERR_CANNOT_CONSTRUCT_INVALID_LENGTH)
            : right(new Matrix(rows, columns, array));
    }
    static rowFromPos3D(pos3D) {
        return new Matrix(1, 3, pos3D.value());
    }
    static columnFromPos3D(pos3D) {
        return new Matrix(3, 1, pos3D.value());
    }
    static columnFromVector(vec) {
        return new Matrix(vec.dimensionality(), 1, vec.value());
    }
    static invalidConstructor(arr, rows, cols) {
        return arr.length !== 0 && arr.length !== rows * cols;
    }
    _contents = [];
    /**
     * initialContents are not validated when called directly
     */
    constructor(_rows, _columns, initialContents = []) {
        this._rows = _rows;
        this._columns = _columns;
        this._contents = this.contents(initialContents, this._rows * this._columns);
    }
    rows() {
        return this._rows;
    }
    columns() {
        return this._columns;
    }
    /**
     * sum two Matrices.
     */
    sum(rightMatrix) {
        if (!this.matchesDimensions(rightMatrix)) {
            return left(Matrix.ERR_CANNOT_ADD_DIMENSIONS_DIFFER);
        }
        return right(new Matrix(this._rows, this._columns, this._contents.map((val, i) => rightMatrix.value()[i] + val)));
    }
    value() {
        return this._contents;
    }
    matchesDimensions(n) {
        return this._rows === n.rows() && this._columns === n.columns();
    }
    scale(scalar) {
        return new Matrix(this._rows, this._columns, this._contents.map((val) => val * scalar));
    }
    row(rowIndex) {
        if (rowIndex < this._rows) {
            let row = new Array(this._columns);
            for (let colIndex = 0; colIndex < this._columns; colIndex++) {
                row[colIndex] = this._contents[rowIndex * this._columns + colIndex];
            }
            return right(new Vector(this._columns, row));
        }
        else {
            return left(Matrix.ERR_INDEX_OUT_OF_BOUNDS);
        }
    }
    column(colIndex) {
        if (colIndex < this._columns) {
            let col = new Array(this._rows);
            for (let rowIndex = 0; rowIndex < this._rows; rowIndex++) {
                col[rowIndex] = this._contents[rowIndex * this._columns + colIndex];
            }
            return right(new Vector(this._rows, col));
        }
        else {
            return left(Matrix.ERR_INDEX_OUT_OF_BOUNDS);
        }
    }
    multiply(rightMatrix) {
        // matrix multiplication can only occur if the number of the left Matrix's columns
        //   are equal to the number of the right Matrix's right
        let colsL = this.columns(), rowsL = this.rows(), colsR = rightMatrix.columns(), rowsR = rightMatrix.rows();
        if (colsL !== rowsR) {
            return left(Matrix.ERR_CANNOT_MULTIPLY_INCOMPATIBLE_DIMENSIONS);
        }
        let product = new Array(rowsL * colsR);
        for (let i = 0; i < rowsL; i++) {
            for (let j = 0; j < colsR; j++) {
                let sum = 0.0;
                for (let k = 0; k < colsL; k++) {
                    sum += this._contents[i * colsL + k] * rightMatrix.value()[k * colsR + j];
                }
                product[i * colsR + j] = sum;
            }
        }
        return Matrix.fromArray(rowsL, colsR, product);
    }
    transpose() {
        const cols = this.columns(), rows = this.rows();
        const transpose = new Array(cols * rows);
        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
                transpose[j * rows + i] = this._contents[i * cols + j];
            }
        }
        return Matrix.fromArray(cols, rows, transpose);
    }
    map(fn) {
        return new Matrix(this.rows(), this.columns(), fn(this.value()));
    }
    /**
     * given an empty array, contents will return an array of the desired length filled with the given value
     * given a non-empty array, contents will simply return the array
     */
    contents(arr, desiredLength) {
        return arr.length === 0 ? new ZeroArray(desiredLength) : arr;
    }
}
export const sum = (r) => (l) => l.sum(r);
export const transpose = (m) => m.transpose();
export const value = (m) => m.value();
//# sourceMappingURL=Matrix.js.map